import * as React from "react"
import { Box, Stack, Typography } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"

import { getCustomerColor } from "./customers"
import { useAdminBrandActions } from "../../@hooks"
import { tableData } from "../../@signals"


// Customer name -> id, read off the loaded rows: `customers` and
// `customerAvailability` are index-aligned, so the pair comes from one walk.
// Feeds the column filter, whose options are customer NAMES (what the user
// reads in the Customers column) while the data holds ids.
const buildCustomerIndex = () => {
    const index = new Map()

    tableData.value.forEach(row => (row.customers || []).forEach((name, position) => {
        const entry = (row.customerAvailability || [])[position]
        if (name && entry && !index.has(name))
            index.set(name, entry.customerID)
    }))

    return index
}


// One column per DIPI_CUSTOMER_BRAND availability flag, with one clickable mark
// per customer — same order and color as the Customers column, so a row reads
// across. The customer's color is kept in BOTH states: a neutral grey for "not
// available" loses which mark belongs to which customer as soon as two flags
// are off. State is carried by shape and intensity instead: solid check when
// available, faded cross when not. Clicking a mark toggles that flag for THAT
// customer only.
export default function availabilityColumn(field, label) {
    const customerIndex = buildCustomerIndex()

    return {
        name: "customerAvailability",
        label: label,
        options: {
            // Filter by customer: pick one and only the brands available to it
            // in THIS column survive. Sort is off — the value is a per-customer
            // list, not a scalar.
            filter: true,
            filterType: "dropdown",
            filterOptions: {
                names: [...customerIndex.keys()].sort(),
                logic: (value, filterValue) => {
                    const selected = (Array.isArray(filterValue) ? filterValue : [filterValue]).filter(Boolean)
                    if (!selected.length) return false

                    const wanted = selected.map(name => String(customerIndex.get(name)))
                    return !(value || []).some(entry =>
                        wanted.includes(String(entry.customerID)) && entry[field]
                    )
                }
            },
            customFilterListOptions: {
                render: name => `${label}: ${name}`
            },
            sort: false,
            setCellHeaderProps: () => ({ style: { textAlign: "center", minWidth: 90 } }),
            setCellProps: () => ({ style: { textAlign: "center" } }),
            customBodyRowRenderLite: (value, row) => <AvailabilityCell row={row} field={field} label={label} />
        }
    }
}

const AvailabilityCell = ({ row = {}, field, label }) => {
    const { updateAvailability, isLoading } = useAdminBrandActions(row)

    const availability = row.customerAvailability || []
    const names = row.customers || []

    if (!availability.length)
        return <Typography color="text.disabled">-</Typography>

    return <Stack
        direction="row"
        spacing={0.5}
        justifyContent="center"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        className={isLoading ? 'is-loading' : ''}
    >
        {availability.map((entry, index) => {
            const isActive = !!entry[field]
            const name = names[index] || entry.customerID

            // Native `title` instead of MUI's Tooltip: one Tooltip per row is
            // expensive on this table and its ref cloning breaks inside these
            // renderers (see CLAUDE.md, blank-page issue).
            return <Box
                key={entry.customerID}
                display="flex"
                alignItems="center"
                title={`${name} — ${label}: ${isActive ? "available" : "not available"} (click to ${isActive ? "disable" : "enable"})`}
                onClick={() => !isLoading && updateAvailability(entry.customerID, field, !isActive)}
                sx={{
                    cursor: isLoading ? 'default' : 'pointer',
                    '&:hover': { transform: isLoading ? 'none' : 'scale(1.15)' }
                }}
            >
                <IconSeeker
                    icon={isActive ? "circle-check" : "circle-xmark"}
                    sx={{
                        fontSize: 19,
                        color: getCustomerColor(index),
                        opacity: isActive ? 1 : 0.45
                    }}
                />
            </Box>
        })}
    </Stack>
}
